import { useTranslation } from 'react-i18next';

import { useFetcher } from '@remix-run/react';
import type { TFunction } from 'i18next';

import { getAppGlobals } from '~/components/AppGlobals/utils';
import useToast, { type ToastData } from '~/hooks/useToast';
import { parseMultipleChoiceValue } from '~/utils/subscriberPreferences/parseMultipleChoiceValue';

interface UsePreferenceActionsProps {
  subscriberId: string;
  isLoggedIn: boolean;
}

const multipleChoiceSeparator = getAppGlobals().VITE_ORCHID_V2_MULTIPLE_CHOICE_SEPARATOR || '||';

const createBaseFormData = (subscriberId: string, t: TFunction) => {
  const formData = new FormData();
  formData.append('preferences_success_message', t('preferences.toast.success'));
  formData.append('preferences_error_message', t('preferences.toast.error'));
  formData.append('subscriber_id', subscriberId);

  return formData;
};

export const usePreferenceActions = ({ subscriberId, isLoggedIn }: UsePreferenceActionsProps) => {
  const { t } = useTranslation();
  const fetcher = useFetcher<{
    toast?: ToastData;
  }>();

  useToast(fetcher?.data?.toast);

  const onSubscriptionPreferenceChange = (enabled: boolean, publicationId: string) => {
    if (!isLoggedIn) return;

    const formData = new FormData();
    formData.append('organization_subscription_success_message', t('preferences.toast.success'));
    formData.append('organization_subscription_error_message', t('preferences.toast.error'));
    formData.append('subscriber_id', subscriberId);
    formData.append('publication_id', publicationId);
    formData.append('subscribed', String(enabled));

    fetcher.submit(formData, {
      method: 'patch',
      action: '/update_subscriber_organization_subscription',
    });
  };

  const onTrueFalsePreferenceChange = (value: boolean, preferenceId: string) => {
    if (!isLoggedIn) return;

    const formData = createBaseFormData(subscriberId, t);

    formData.append('preference_value', String(value));
    formData.append('preference_id', preferenceId);

    fetcher.submit(formData, {
      method: 'patch',
      action: '/update_subscriber_preference',
    });
  };

  const onShortResponsePreferenceChange = (value: string, preferenceId: string) => {
    if (!isLoggedIn) return;

    const formData = createBaseFormData(subscriberId, t);

    formData.append('preference_value', value);
    formData.append('preference_id', preferenceId);

    fetcher.submit(formData, {
      method: 'patch',
      action: '/update_subscriber_preference',
    });
  };

  const onDropdownPreferenceChange = (value: string, preferenceId: string) => {
    if (!isLoggedIn) return;

    const formData = createBaseFormData(subscriberId, t);

    formData.append('preference_value', value);
    formData.append('preference_id', preferenceId);

    fetcher.submit(formData, {
      method: 'patch',
      action: '/update_subscriber_preference',
    });
  };

  const onMultipleChoicePreferenceChange = (values: string[], preferenceId: string) => {
    if (!isLoggedIn) return;

    const formData = createBaseFormData(subscriberId, t);

    formData.append('preference_value', values.join(multipleChoiceSeparator));
    formData.append('preference_id', preferenceId);

    fetcher.submit(formData, {
      method: 'patch',
      action: '/update_subscriber_preference',
    });
  };

  const onSingleChoicePreferenceChange = (value: string, preferenceId: string) => {
    if (!isLoggedIn) return;

    const formData = createBaseFormData(subscriberId, t);

    formData.append('preference_value', value);
    formData.append('preference_id', preferenceId);

    fetcher.submit(formData, {
      method: 'patch',
      action: '/update_subscriber_preference',
    });
  };

  return {
    isLoading: fetcher.state === 'submitting',
    onSubscriptionPreferenceChange,
    onTrueFalsePreferenceChange,
    onShortResponsePreferenceChange,
    onDropdownPreferenceChange,
    onMultipleChoicePreferenceChange,
    onSingleChoicePreferenceChange,
    parseMultipleChoiceValue,
  };
};
